import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Icon from '../Icon';
import usePageMeta from '../usePageMeta';
import useJsonLd from '../useJsonLd';
import { contactLink } from '../topics';
import { useSiteContent, useContact } from '../SiteContent';
import { activeSite } from '../sites';
import { ortBySlug } from '../orte';
import ContactSection from '../components/ContactSection';
import T from '../components/EditableText';
import StepList from '../components/StepList';
import FaqSection from '../components/FaqSection';
import CtaBand from '../components/CtaBand';
import PageHero from '../components/PageHero';
import { webpOf } from '../components/HeroPicture';
import FactBox from '../components/FactBox';
import NotFound from './NotFound';

/**
 * Ortsseite der 24-Stunden-Betreuung: `/24h-betreuung/<ort>`.
 *
 * Eine Komponente für alle Orte, aber je Ort eigener Text (siehe `orte.ts`
 * und `content/ortsseiten.ts`). Aufbau bewusst anders als die Hauptseite
 * `/24h-betreuung`: kürzer, mit dem Ort in Überschrift, Text und Fragen —
 * die Seite soll für „24 stunden pflege wolfsburg" die passendere Antwort sein
 * als die allgemeine Seite, nicht deren Kopie.
 *
 * Strukturierte Daten: `Service` mit `areaServed` auf den Ort, dazu `FAQPage`
 * aus den sichtbaren Fragen und `BreadcrumbList` (Startseite → 24h → Ort).
 */
const FAQ_NUMBERS = [1, 2, 3, 4, 5];
const points = (prefix: string, count: number): string[] =>
  Array.from({ length: count }, (_, index) => `${prefix}.punkt-${index + 1}`);
/** Textschlüssel eines Orts: `ort.wolfsburg.hero.titel`. */
const keyFor = (slug: string, rest: string): string => `ort.${slug}.${rest}`;

const Ortsseite24h: React.FC = () => {
  const { ort: slug } = useParams<{ ort: string }>();
  const ort = ortBySlug(slug);
  if (!ort) return <NotFound />;
  return <OrtsseiteInhalt slug={ort.slug} />;
};

/** Getrennt, damit die Hooks erst laufen, wenn der Ort feststeht. */
const OrtsseiteInhalt: React.FC<{ slug: string }> = ({ slug }) => {
  const ort = ortBySlug(slug)!;
  const { plain } = useSiteContent();
  const contact = useContact();
  const k = (rest: string) => keyFor(ort.slug, rest);

  usePageMeta(
    `24-Stunden-Betreuung in ${ort.name} | Pflege zu Hause | Antczak`,
    `24-Stunden-Pflege zu Hause in ${ort.name}: Betreuungskraft wohnt im Haushalt, Erstgespräch bei Ihnen vor Ort. Ab 100 € am Tag, Beratung aus Gifhorn.`,
    { standaloneTitle: true, image: ort.image, preloadImage: webpOf(ort.image) },
  );

  const jsonLd = React.useMemo(() => {
    const base = activeSite().url;
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${base}/24h-betreuung/${ort.slug}#service`,
          name: `24-Stunden-Betreuung in ${ort.name}`,
          serviceType: '24-Stunden-Betreuung zu Hause',
          description: plain(keyFor(ort.slug, 'hero.absatz-1')),
          provider: { '@id': `${base}/#organisation` },
          areaServed: { '@type': 'City', name: ort.name },
          url: `${base}/24h-betreuung/${ort.slug}`,
          offers: {
            '@type': 'Offer',
            priceCurrency: 'EUR',
            price: '100',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: '100', priceCurrency: 'EUR', unitText: 'Tag', minPrice: '100' },
            url: `${base}/kosten`,
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQ_NUMBERS.map((number) => ({
            '@type': 'Question',
            name: plain(keyFor(ort.slug, `faq.${number}.frage`)),
            acceptedAnswer: { '@type': 'Answer', text: plain(keyFor(ort.slug, `faq.${number}.antwort`)) },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Startseite', item: `${base}/` },
            { '@type': 'ListItem', position: 2, name: '24-Stunden-Betreuung', item: `${base}/24h-betreuung` },
            { '@type': 'ListItem', position: 3, name: ort.name, item: `${base}/24h-betreuung/${ort.slug}` },
          ],
        },
      ],
    };
  }, [ort, plain]);

  useJsonLd(`ort-${ort.slug}`, jsonLd);

  return (
    <>
      <PageHero
        eyebrowKey={k('hero.eyebrow')}
        titleKey={k('hero.titel')}
        accentKey={k('hero.titel-akzent')}
        image={{ src: ort.image, alt: `Betreuerin hält die Hand einer Seniorin — 24-Stunden-Betreuung in ${ort.name}`, width: 900, height: 1000, position: 'center 35%' }}
        above={
          <nav className="breadcrumb" aria-label="Sie sind hier">
            <Link to="/">Startseite</Link>
            <span aria-hidden="true">›</span>
            <Link to="/24h-betreuung">24-Stunden-Betreuung</Link>
            <span aria-hidden="true">›</span>
            <span>{ort.name}</span>
          </nav>
        }
      >
        <p className="hx-hero__lead"><T k={k('hero.absatz-1')} /></p>
        <div className="btn-row hx-hero__actions">
          <Link to={contactLink('CARE_24H')} className="btn btn--primary">
            <T k={k('hero.cta-beratung')} />
          </Link>
          <a href={contact.phoneHref} className="btn btn--on-dark" data-stelle="hero">
            <Icon name="phone" size={18} />
            {contact.phoneLabel}
          </a>
          <a href={contact.whatsappHref} className="btn btn--on-dark" target="_blank" rel="noopener noreferrer" data-stelle="hero">
            <Icon name="message-circle" size={18} />
            <T k={k('hero.cta-whatsapp')} />
          </a>
        </div>
      </PageHero>

      <section className="shell hx-facts-wrap">
        <FactBox prefix={k('fakten')} />
      </section>

      <section className="shell section">
        <div className="prose hx-lede">
          <p><T k={k('hero.absatz-2')} /></p>
        </div>
      </section>

      {/* Ablauf in sechs Schritten */}
      <section className="section--tinted">
        <div className="shell section hx-steps">
          <div className="hx-steps__head">
            <h2 className="h-section"><T k={k('ablauf.titel')} /></h2>
            <p><T k={k('ablauf.einleitung')} /></p>
          </div>
          <StepList keys={points(k('ablauf'), 6)} />
        </div>
      </section>

      {/* Leistungen */}
      <section className="shell hx-feature">
        <div className="hx-feature__media">
          <img src="/website/img/24h-grundpflege.jpg" alt="Betreuerin hilft einer Seniorin beim Aufstehen" width={760} height={620} loading="lazy" />
        </div>
        <div className="hx-feature__body">
          <h2 className="h-block"><T k={k('leistungen.titel')} /></h2>
          <p className="hx-feature__lead"><T k={k('leistungen.einleitung')} /></p>
          <ul className="hx-checks">
            {points(k('leistungen'), 6).map((key) => (
              <li key={key}><Icon name="check" size={17} /><span><T k={key} /></span></li>
            ))}
          </ul>
        </div>
      </section>

      <CtaBand titleKey="betreuung24h.banner.titel" textKey="betreuung24h.banner.text" />

      {/* Ortsteile — echter Inhalt, kein Schlagwortteppich: eine Liste, die der
          Besucher aus Fallersleben tatsächlich liest, um sich wiederzufinden. —
          daneben die Kosten mit Verweis auf die Kostenseite. */}
      <section className="shell section hx-twocol">
        <div>
          <h2 className="h-block"><T k={k('ortsteile.titel')} /></h2>
          <p className="hx-muted-lead"><T k={k('ortsteile.text')} /></p>
          <ul className="chip-list" aria-label={`Ortsteile von ${ort.name}`}>
            {ort.ortsteile.map((teil) => (
              <li key={teil} className="chip">{teil}</li>
            ))}
          </ul>
        </div>
        <div className="hx-note">
          <span className="hx-note__mark"><Icon name="wallet" size={18} /> <T k="navigation.kosten" /></span>
          <h2 className="h-card"><T k={k('kosten.titel')} /></h2>
          <p style={{ marginBottom: 16 }}><T k={k('kosten.text')} /></p>
          <Link to="/kosten" className="textlink">
            <T k={k('kosten.cta')} /> <Icon name="arrow-right" size={16} />
          </Link>
        </div>
      </section>

      <FaqSection titleKey={k('faq.titel')} numbers={FAQ_NUMBERS} keyOf={(n) => k(`faq.${n}`)} />

      <ContactSection topic="CARE_24H" />
    </>
  );
};

export default Ortsseite24h;
